/**
 * Milcotec search
 *
 * Predictive search has been disabled intentionally.
 *
 * Customers submit their query directly to Shopify's
 * full product search.
 *
 * Numeric full-search enhancement:
 *
 * 1. Exact/plain SKU
 * 2. Plain SKU beginning with number
 * 3. D-prefixed SKU
 * 4. U-prefixed SKU
 * 5. FW-prefixed SKU
 * 6. Normal Shopify matches
 *
 * Examples:
 *
 * 17860 -> 17860
 * 1084  -> 10840 + D1084...
 * 9410  -> FW9410
 *
 * No predictive-search AJAX requests are made while typing.
 */

if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.is_drawer = this.dataset.drawer;
      this.drawer = this.closest('#Search-Drawer');

      this.form = this.querySelector('form');
      this.button = document.querySelectorAll('.thb-quick-search');
      this.input = this.querySelector('.search-field');

      this.cc = this.querySelector(
        '.searchform--click-capture'
      );

      this.promotionTemplate = this.querySelector(
        '.thb-predictive-search--promotion-template'
      );

      this.setupEventListeners();

      /*
       * Run the full-search enhancement only once.
       *
       * Wait until DOMContentLoaded if necessary because
       * the header search-form can be initialized before
       * the main search grid exists in the DOM.
       */
      if (!window.__milcotecFullSearchScheduled) {
        window.__milcotecFullSearchScheduled = true;

        const runFullSearchEnhancement = () => {
          this.enhanceFullSearchResults();
        };

        if (document.readyState === 'loading') {
          document.addEventListener(
            'DOMContentLoaded',
            runFullSearchEnhancement,
            { once: true }
          );
        } else {
          runFullSearchEnhancement();
        }
      }
    }

    /**
     * Hydrate optional drawer promotion products.
     *
     * This is NOT predictive search.
     * It only loads the configured static promotion
     * content when the search drawer is opened.
     */
    hydratePromotion() {
      if (!this.promotionTemplate) {
        return;
      }

      this.promotionTemplate.parentNode.insertBefore(
        this.promotionTemplate.content,
        this.promotionTemplate
      );

      this.promotionTemplate.remove();
      this.promotionTemplate = null;
    }

    setupEventListeners() {
      /*
       * Submit only.
       *
       * There are deliberately NO input/focus listeners
       * and NO predictive-search requests.
       */
      if (this.form) {
        this.form.addEventListener(
          'submit',
          this.onFormSubmit.bind(this)
        );
      }

      /*
       * Existing Vision search-button behaviour.
       */
      this.button.forEach((item) => {
        item.addEventListener(
          'click',
          (event) => {
            event.preventDefault();

            this.hydratePromotion();

            if (this.is_drawer && this.drawer) {
              OverlayManager.open(this.drawer);

              setTimeout(() => {
                if (this.input) {
                  this.input.focus({
                    preventScroll: true
                  });
                }
              }, 100);
            } else {
              document.body.classList.add(
                'open-cc'
              );

              setTimeout(() => {
                if (this.input) {
                  this.input.focus({
                    preventScroll: true
                  });
                }
              }, 100);
            }

            return false;
          }
        );
      });

      /*
       * Inline desktop search close behaviour.
       */
      if (!this.is_drawer) {
        this.onCcClickBound =
          this.close.bind(this);

        this.onKeyUpBound = (event) => {
          if (
            event.code &&
            event.code.toUpperCase() === 'ESCAPE'
          ) {
            this.close();
          }
        };

        if (this.cc) {
          this.cc.addEventListener(
            'click',
            this.onCcClickBound
          );
        }

        document.addEventListener(
          'keyup',
          this.onKeyUpBound
        );
      }
    }

    disconnectedCallback() {
      if (this.onKeyUpBound) {
        document.removeEventListener(
          'keyup',
          this.onKeyUpBound
        );
      }

      if (
        this.onCcClickBound &&
        this.cc
      ) {
        this.cc.removeEventListener(
          'click',
          this.onCcClickBound
        );
      }
    }

    getQuery() {
      if (!this.input) {
        return '';
      }

      return this.input.value.trim();
    }

    /**
     * Submit exactly what the customer typed.
     *
     * type=product is supplied by the hidden input
     * in the Liquid search forms.
     */
    onFormSubmit(event) {
      const searchTerm =
        this.getQuery();

      if (!searchTerm.length) {
        event.preventDefault();
        return;
      }

      this.input.value =
        searchTerm;
    }

    /**
     * Read SKU from a rendered product card.
     */
    getSkuFromElement(element) {
      if (!element) {
        return '';
      }

      const skuElement =
        element.querySelector(
          '[data-sku], .product-card-sku, .sku, [class*="sku"]'
        );

      if (skuElement) {
        const dataSku =
          skuElement.getAttribute(
            'data-sku'
          );

        if (dataSku) {
          return dataSku
            .replace(/^SKU:\s*/i, '')
            .trim()
            .toUpperCase();
        }

        const text =
          skuElement.textContent || '';

        const cleaned =
          text
            .replace(/^SKU:\s*/i, '')
            .trim()
            .toUpperCase();

        if (cleaned) {
          return cleaned;
        }
      }

      const fullText =
        (element.textContent || '')
          .replace(/\s+/g, ' ')
          .trim();

      const match =
        fullText.match(
          /(?:^|\s)SKU:\s*([A-Z0-9.-]+)/i
        );

      return match
        ? match[1]
            .trim()
            .toUpperCase()
        : '';
    }

    /**
     * Get product URL for duplicate detection.
     */
    getProductPath(element) {
      if (!element) {
        return '';
      }

      const link =
        element.querySelector(
          'a[href*="/products/"]'
        );

      if (!link) {
        return '';
      }

      try {
        return new URL(
          link.href,
          window.location.origin
        ).pathname;
      } catch (error) {
        return '';
      }
    }

    /**
     * SKU ranking for numeric searches.
     *
     * Search: 220
     *
     * 220       -> exact
     * 2200      -> plain prefix
     * 22020     -> plain prefix
     * D220...   -> D prefix
     * U220...   -> U prefix
     * FW220...  -> FW prefix
     */
    getSkuPriority(
      sku,
      searchTerm
    ) {
      const term =
        searchTerm
          .trim()
          .toUpperCase();

      const normalizedSku =
        (sku || '')
          .trim()
          .toUpperCase();

      if (
        !term ||
        !normalizedSku
      ) {
        return 100;
      }

      if (
        normalizedSku === term
      ) {
        return 0;
      }

      if (
        normalizedSku.startsWith(
          term
        )
      ) {
        return 10;
      }

      if (
        normalizedSku.startsWith(
          `D${term}`
        )
      ) {
        return 20;
      }

      if (
        normalizedSku.startsWith(
          `U${term}`
        )
      ) {
        return 30;
      }

      if (
        normalizedSku.startsWith(
          `FW${term}`
        )
      ) {
        return 40;
      }

      return 100;
    }

    sortProductItems(
      items,
      searchTerm
    ) {
      return items
        .map((item, index) => {
          const sku =
            this.getSkuFromElement(
              item
            );

          return {
            item,
            index,
            priority:
              this.getSkuPriority(
                sku,
                searchTerm
              )
          };
        })
        .sort((a, b) => {
          if (
            a.priority !==
            b.priority
          ) {
            return (
              a.priority -
              b.priority
            );
          }

          return (
            a.index -
            b.index
          );
        })
        .map(
          (entry) => entry.item
        );
    }

    /**
     * Fetch one product-only Shopify search section.
     *
     * This is used ONLY on the full /search page.
     */
    async fetchFullSearchSection(
      searchTerm,
      sectionId
    ) {
      const url =
        new URL(
          window.location.pathname,
          window.location.origin
        );

      url.searchParams.set(
        'q',
        searchTerm
      );

      url.searchParams.set(
        'type',
        'product'
      );

      url.searchParams.set(
        'options[prefix]',
        'last'
      );

      url.searchParams.set(
        'section_id',
        sectionId
      );

      const response =
        await fetch(
          url.toString(),
          {
            credentials:
              'same-origin'
          }
        );

      if (!response.ok) {
        throw new Error(
          `Full search failed: ${response.status}`
        );
      }

      const html =
        await response.text();

      return new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );
    }

    /**
     * Enhance numeric full searches.
     *
     * Example:
     *
     * /search?type=product&q=1084
     *
     * Shopify's normal result is kept.
     *
     * We additionally search:
     *
     * D1084
     * U1084
     * FW1084
     *
     * and merge unique product cards into the grid.
     */
    async enhanceFullSearchResults() {
      const grid =
        document.querySelector(
          '[data-milcotec-search-grid]'
        );

      if (!grid) {
        return;
      }

      const params =
        new URLSearchParams(
          window.location.search
        );

      const searchTerm =
        (params.get('q') || '')
          .trim();

      /*
       * Enhancement is only needed for
       * pure numeric searches.
       */
      if (
  !/^[A-Za-z0-9][A-Za-z0-9._/-]*$/.test(searchTerm) ||
  !/\d/.test(searchTerm) ||
  searchTerm.length < 3
) {
  return;
}

      const sectionId =
        grid.dataset.sectionId;

      if (!sectionId) {
        return;
      }

      const prefixTerms = [
  `variants.sku:${searchTerm}`,
  `tag:${searchTerm}`
];

if (/^\d{3,}$/.test(searchTerm)) {
  prefixTerms.push(
    `D${searchTerm}`,
    `U${searchTerm}`,
    `FW${searchTerm}`
  );
}

      const knownProducts =
        new Set();

      /*
       * Register products already returned
       * by Shopify.
       */
      Array.from(
        grid.querySelectorAll(
          ':scope > li.column'
        )
      ).forEach((item) => {
        const path =
          this.getProductPath(
            item
          );

        if (path) {
          knownProducts.add(path);
        }
      });

      const loadingOverlay =
        grid.querySelector(
          ':scope > .loading-overlay'
        );

      try {
        /*
         * Only three additional requests are made,
         * and only after the customer has loaded
         * a numeric full-search results page.
         */
        const documents =
          await Promise.all(
            prefixTerms.map((term) => {
              return this.fetchFullSearchSection(
                term,
                sectionId
              );
            })
          );

        documents.forEach((parsed) => {
          const fetchedGrid =
            parsed.querySelector(
              '[data-milcotec-search-grid]'
            );

          if (!fetchedGrid) {
            return;
          }

          const items =
            Array.from(
              fetchedGrid.querySelectorAll(
                ':scope > li.column'
              )
            );

          items.forEach((item) => {
            const path =
              this.getProductPath(
                item
              );

            if (!path) {
              return;
            }

            if (
              knownProducts.has(path)
            ) {
              return;
            }

            knownProducts.add(path);

            const cloned =
              document.importNode(
                item,
                true
              );

            if (loadingOverlay) {
              grid.insertBefore(
                cloned,
                loadingOverlay
              );
            } else {
              grid.appendChild(
                cloned
              );
            }
          });
        });

        /*
         * Put actual SKU matches ahead of
         * ordinary title/text matches.
         */
        this.sortFullSearchResults(
          searchTerm,
          grid
        );

        const visibleProducts =
          Array.from(
            grid.querySelectorAll(
              ':scope > li.column'
            )
          ).filter((item) => {
            return Boolean(
              item.querySelector(
                'a[href*="/products/"]'
              )
            );
          });

        /*
         * Shopify can return zero for something
         * like 9410 while FW9410 exists.
         *
         * main-search.liquid supplies a hidden
         * empty grid specifically for this case.
         */
        if (
          visibleProducts.length
        ) {
          const emptyState =
            document.querySelector(
              '[data-milcotec-search-empty]'
            );

          if (emptyState) {
            emptyState.style.display =
              'none';
          }

          grid.hidden = false;
        }

        /*
         * Do not alter Shopify's count when
         * pagination exists.
         *
         * On a zero-result/non-paginated search
         * such as 9410 -> FW9410, update the
         * visible count to reflect the merged cards.
         */
        const hasPagination =
          Boolean(
            document.querySelector(
              'a[href*="page=2"], .pagination a[href*="page="], nav[role="navigation"] a[href*="page="]'
            )
          );

        if (!hasPagination) {
          this.updateFullSearchCount(
            searchTerm,
            grid
          );
        }
      } catch (error) {
        console.error(
          'Milcotec full search enhancement error:',
          error
        );
      }
    }

    /**
     * Sort currently rendered products.
     */
    sortFullSearchResults(
      searchTerm,
      grid
    ) {
      const items =
        Array.from(
          grid.querySelectorAll(
            ':scope > li.column'
          )
        ).filter((item) => {
          return Boolean(
            item.querySelector(
              'a[href*="/products/"]'
            )
          );
        });

      if (!items.length) {
        return;
      }

      const sorted =
        this.sortProductItems(
          items,
          searchTerm
        );

      const loadingOverlay =
        grid.querySelector(
          ':scope > .loading-overlay'
        );

      sorted.forEach((item) => {
        if (loadingOverlay) {
          grid.insertBefore(
            item,
            loadingOverlay
          );
        } else {
          grid.appendChild(
            item
          );
        }
      });
    }

    /**
     * Correct visible count on non-paginated
     * searches after D/U/FW products were merged.
     */
    updateFullSearchCount(
      searchTerm,
      grid
    ) {
      const productCount =
        Array.from(
          grid.querySelectorAll(
            ':scope > li.column'
          )
        ).filter((item) => {
          return Boolean(
            item.querySelector(
              'a[href*="/products/"]'
            )
          );
        }).length;

      const normalizedTerm =
        searchTerm
          .trim()
          .toLowerCase();

      const candidates =
        Array.from(
          document.querySelectorAll(
            'p, span, div'
          )
        );

      candidates.forEach((element) => {
        if (
          element.children.length
        ) {
          return;
        }

        const text =
          (
            element.textContent ||
            ''
          ).trim();

        if (!text) {
          return;
        }

        if (
          !/^\d+\s/.test(text)
        ) {
          return;
        }

        if (
          !text
            .toLowerCase()
            .includes(
              normalizedTerm
            )
        ) {
          return;
        }

        let updatedText =
          text.replace(
            /^\d+/,
            String(productCount)
          );

        if (
          productCount === 1
        ) {
          updatedText =
            updatedText.replace(
              /\bresults\b/i,
              'result'
            );
        } else {
          updatedText =
            updatedText.replace(
              /\bresult\b(?!s)/i,
              'results'
            );
        }

        element.textContent =
          updatedText;
      });
    }

    /**
     * Close Vision inline search UI.
     */
    close() {
      document.body.classList.remove(
        'open-search'
      );

      document.body.classList.remove(
        'open-cc'
      );
    }
  }

  customElements.define(
    'search-form',
    SearchForm
  );
}